import {
  CalendarClock,
  CreditCard,
  Clock,
  DoorOpen,
  FileSignature,
  Grid2x2,
  LayoutDashboard,
  MonitorUp,
  Package,
  Settings,
  Ticket,
  Users,
  type LucideIcon,
} from 'lucide-react';

/** Labeled sidebar sections. Order of rendering is {@link NAV_GROUP_ORDER}. */
export type NavGroup = 'operations' | 'catalog' | 'configuration';

/** Group label i18n keys, keyed by group id; rendered as `SidebarGroupLabel`. */
export const NAV_GROUP_LABEL_KEYS: Record<NavGroup, string> = {
  operations: 'nav.group.operations',
  catalog: 'nav.group.catalog',
  configuration: 'nav.group.configuration',
};

/** Render order of the labeled groups. Ungrouped items (home) render first, label-less. */
export const NAV_GROUP_ORDER: NavGroup[] = ['operations', 'catalog', 'configuration'];

export type NavItem = {
  /** Sub-path under `/loc/:venueId/`; empty string is the workspace home. */
  path: string;
  labelKey: string;
  icon: LucideIcon;
  /** Permission codes that reveal the item at the current venue; omit for always-visible. */
  permissions?: string[];
  /** Match the route exactly (used for the workspace home link). */
  end?: boolean;
  /** Labeled section the item belongs to; omit to render ungrouped at the top (home). */
  group?: NavGroup;
  /**
   * Builds an absolute app URL outside `/loc/:venueId/` (e.g. the public waiver
   * signing page). When set, the item opens in a new tab instead of routing via `path`.
   */
  externalPath?: (venueId: string) => string;
};

/**
 * Venue-bound nav. Each item is gated at the *current* venue's scope, so the same
 * sidebar renders differently per venue for a user with per-venue roles (ADR 0015).
 * Shared by `VenueShell` (rendering) and the switcher's switch-target computation (deciding
 * whether a feature survives a venue switch).
 */
export const NAV_ITEMS: NavItem[] = [
  { path: '', labelKey: 'workspace.home', icon: LayoutDashboard, end: true },
  {
    path: 'bookings',
    labelKey: 'nav.bookings',
    icon: CalendarClock,
    permissions: ['booking.view'],
    group: 'operations',
  },
  {
    path: 'capacity',
    labelKey: 'nav.capacity',
    icon: Grid2x2,
    permissions: ['venue.capacity_overview.view'],
    group: 'operations',
  },
  {
    path: 'customers',
    labelKey: 'nav.customers',
    icon: Users,
    permissions: ['customer.view'],
    group: 'operations',
  },
  {
    path: 'mvp-card-numbers',
    labelKey: 'nav.mvpCardNumbers',
    icon: CreditCard,
    permissions: ['mvp.sell'],
    group: 'operations',
  },
  {
    path: 'shifts',
    labelKey: 'nav.shifts',
    icon: Clock,
    permissions: ['workforce.shift.view', 'workforce.shift.manage'],
    group: 'operations',
  },
  {
    path: 'products',
    labelKey: 'nav.products',
    icon: Ticket,
    permissions: ['products.pass.view', 'products.pass.manage'],
    group: 'catalog',
  },
  {
    path: 'inventory',
    labelKey: 'nav.inventory',
    icon: Package,
    permissions: ['products.inventory.view', 'products.inventory.manage'],
    group: 'catalog',
  },
  {
    path: 'rooms',
    labelKey: 'nav.rooms',
    icon: DoorOpen,
    permissions: ['venue.room.manage'],
    group: 'configuration',
  },
  {
    path: 'booking-settings',
    labelKey: 'nav.bookingSettings',
    icon: Settings,
    permissions: ['venue.booking_settings.view', 'venue.booking_settings.manage'],
    group: 'configuration',
  },
  {
    path: 'operating-hours',
    labelKey: 'nav.operatingHours',
    icon: Settings,
    permissions: ['venue.operating_hours.view', 'venue.operating_hours.manage'],
    group: 'configuration',
  },
  {
    path: 'waiver',
    labelKey: 'nav.waiver',
    icon: FileSignature,
    permissions: ['venue.waiver_assignment.manage'],
    group: 'configuration',
  },
  {
    path: 'waiver-signing',
    labelKey: 'nav.waiverSigning',
    icon: FileSignature,
    permissions: ['venue.booking_settings.view', 'venue.booking_settings.manage'],
    group: 'configuration',
    externalPath: (venueId) =>
      new URL(`/public/waivers/${venueId}/sign`, window.location.origin).toString(),
  },
  {
    path: 'pos-devices',
    labelKey: 'nav.posDevices',
    icon: MonitorUp,
    permissions: ['pos.installation.enroll'],
    group: 'configuration',
  },
  {
    path: 'pos-stations',
    labelKey: 'nav.posStations',
    icon: MonitorUp,
    permissions: ['pos.device.manage'],
    group: 'configuration',
  },
];
